import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    ListSubheader,
    MenuItem,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    CalendarToday as CalendarTodayIcon,
    Check as CheckIcon,
    DeleteOutline as DeleteOutlineIcon,
    Notes as NotesIcon,
    Storefront as StorefrontIcon,
} from '@mui/icons-material';
import {
    accountsApi,
    activitiesApi,
    projectAccountsApi,
    projectsApi,
    suppliersApi,
    transactionsApi,
} from '../../services/api';
import { formatSupplierForAutocomplete } from '../../utils/supplierFormatter';
import {
    TRANSACTION_TYPES_BY_ID,
    transactionTypeLabelKey,
    resolveTargetProjectAccount,
} from '../../constants/transactionTypes';
import { projectAccountTypeLabelKey } from '../../constants/projectAccountTypes';
import { useDebouncedSearchInput } from '../../hooks/useDebouncedSearchInput';
import {
    signedAllocationsTotal,
    allocationsBalanceHeader,
    directionFromSignedValue,
    TRANSACTION_EXPENSE,
} from '../../utils/transactionAmount';
import { TransactionAmountWithDirection, TransactionDirectionBadge, TransactionDirectionToggleGroup } from './TransactionDirectionDisplay';

// Business-event types offered in the wizard. `custom` lets the user pick the
// direction and project accounts themselves (no predetermined type).
const WIZARD_TXN_TYPES = [
    'pay_supplier',
    'receive_supplier_refund',
    'record_customer_funds_deposit',
    'receive_customer_payment',
    'receive_owner_contribution',
    'custom',
];

const LIST_PAGE_SIZE = 500;

const fmtMoney = (value) => {
    const n = Number(value) || 0;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatTxnDate = (value) => {
    if (!value) return '—';
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [year, month, day] = raw.split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString();
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? raw.slice(0, 10) : parsed.toLocaleDateString();
};

function CompactTransactionCard({ title, date, amount, supplier, project, description, warning }) {
    return (
        <Card variant="outlined" sx={warning ? { borderColor: 'warning.main' } : undefined}>
            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Typography variant="subtitle2" gutterBottom>
                    {title}
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">{date.label}</Typography>
                        <Typography variant="body2">{date.value}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">{amount.label}</Typography>
                        <Typography variant="body2">{amount.value}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">{supplier.label}</Typography>
                        <Typography variant="body2">{supplier.value}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">{project.label}</Typography>
                        <Typography variant="body2">{project.value}</Typography>
                    </Grid>
                    {description?.value ? (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">{description.label}</Typography>
                            <Typography variant="body2">{description.value}</Typography>
                        </Grid>
                    ) : null}
                </Grid>
            </CardContent>
        </Card>
    );
}

const today = () => new Date().toISOString().split('T')[0];

let allocationSeq = 0;
const makeAllocation = (defaultDirection = TRANSACTION_EXPENSE) => ({
    key: `alloc-${allocationSeq++}`,
    project: '',
    projectAccountId: '',
    activity: '',
    isMaterials: false,
    amount: '',
    direction: defaultDirection,
});

/**
 * New multi-step "Add Transaction" flow (Phase 5+). The transaction header holds
 * date, financial account, transaction type (which sets the direction), supplier,
 * description, and total amount. Step 2 splits that amount across one or more
 * project-account allocations (each with its own activity/materials). The result
 * is a single transaction with multiple project transactions.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => void} [props.onSaved]
 * @param {string|null} [props.defaultAccount]  Preselected financial account name.
 * @param {string|null} [props.defaultProjectId]  Preselected project for the first allocation.
 * @param {Array<{project_id: (string|number), project_account_id: (string|number), amount: (string|number)}>|null} [props.defaultAllocations]
 *   Preseeded allocations (one per row). Overrides defaultProjectId when provided.
 * @param {string|null} [props.defaultDate]  ISO date (YYYY-MM-DD).
 * @param {string|null} [props.defaultAmount]  Total amount string/number.
 * @param {string|null} [props.defaultDescription]
 * @param {string|number|null} [props.defaultSupplierId]
 * @param {string|null} [props.defaultTransactionType]  e.g. pay_supplier, receive_supplier_refund.
 * @param {string|null} [props.titleOverride]  Custom dialog title.
 * @param {React.ReactNode} [props.infoBanner]  Optional content above the stepper.
 * @param {(payload: object) => { allowed: boolean, blockers?: object[] }|void} [props.beforeSubmit]
 * @param {(response: object) => void|Promise<void>} [props.onSubmitResponse]
 *   When set, called with the createAllocated API response instead of only onSaved.
 */
export default function AddTransactionWizardModal({
    open,
    onClose,
    onSaved,
    defaultAccount = null,
    defaultProjectId = null,
    defaultAllocations = null,
    defaultDate = null,
    defaultAmount = null,
    defaultDescription = null,
    defaultSupplierId = null,
    defaultTransactionType = null,
    titleOverride = null,
    infoBanner = null,
    beforeSubmit = null,
    onSubmitResponse = null,
}) {
    const { t } = useTranslation();

    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [similarTransactions, setSimilarTransactions] = useState([]);

    const [accounts, setAccounts] = useState([]);
    const [projects, setProjects] = useState([]);
    const [activities, setActivities] = useState([]);
    const [projectAccountsByProject, setProjectAccountsByProject] = useState({});

    // Suppliers use debounced server-side search + infinite scroll so that all
    // suppliers are reachable (not just the first page).
    const [suppliers, setSuppliers] = useState([]);
    const [supplierInput, setSupplierInput] = useState('');
    const [supplierInputDisplay, setSupplierInputDisplay] = useState('');
    const supplierSearchDebounced = useDebouncedSearchInput(supplierInput, 400);
    const [suppliersHasMore, setSuppliersHasMore] = useState(true);
    const [suppliersLoadingMore, setSuppliersLoadingMore] = useState(false);
    const [suppliersRefreshing, setSuppliersRefreshing] = useState(false);
    const nextSupplierPageRef = useRef(2);
    const supplierSeqRef = useRef(0);
    // Keep the currently-selected supplier merged into the list so it stays
    // visible even after searching for something else.
    const selectedSupplierIdRef = useRef(null);

    const [activityInput, setActivityInput] = useState('');
    const activitySearchDebounced = useDebouncedSearchInput(activityInput, 400);
    const [activitiesHasMore, setActivitiesHasMore] = useState(true);
    const [activitiesLoadingMore, setActivitiesLoadingMore] = useState(false);
    const [activitiesRefreshing, setActivitiesRefreshing] = useState(false);
    const nextActivityPageRef = useRef(2);
    const activitySeqRef = useRef(0);
    const selectedActivityIdsRef = useRef([]);

    // Step 1 header
    const [date, setDate] = useState(today());
    const [account, setAccount] = useState('');
    const [transactionType, setTransactionType] = useState('');
    const [customDirection, setCustomDirection] = useState('expense');
    const [supplier, setSupplier] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');

    // Step 2 allocations
    const [allocations, setAllocations] = useState([makeAllocation()]);

    const typeSpec = transactionType ? TRANSACTION_TYPES_BY_ID[transactionType] : null;
    const isCustomType = Boolean(typeSpec?.custom);
    const requiresSupplier = Boolean(typeSpec?.requiresSupplier);
    // Predetermined types fix the direction; custom lets the user choose.
    const direction = isCustomType ? customDirection : (typeSpec?.direction || null);

    const resetState = useCallback(() => {
        setActiveStep(0);
        setError(null);
        setSimilarTransactions([]);
        setDate(defaultDate || today());
        setAccount(defaultAccount || '');
        setTransactionType(defaultTransactionType || '');
        setCustomDirection('expense');
        const seededSupplier = defaultSupplierId != null && defaultSupplierId !== '' ? String(defaultSupplierId) : '';
        setSupplier(seededSupplier);
        selectedSupplierIdRef.current = seededSupplier || null;
        setSupplierInput('');
        setSupplierInputDisplay('');
        setActivityInput('');
        selectedActivityIdsRef.current = [];
        setDescription(defaultDescription || '');
        setAmount(defaultAmount != null && defaultAmount !== '' ? String(defaultAmount) : '');
        const seedTypeSpec = defaultTransactionType ? TRANSACTION_TYPES_BY_ID[defaultTransactionType] : null;
        const seedDirection = seedTypeSpec?.custom
            ? TRANSACTION_EXPENSE
            : (seedTypeSpec?.direction || TRANSACTION_EXPENSE);
        // Seed one allocation per mapped split when provided (e.g. QuickBooks create),
        // otherwise a single blank allocation prefilled with the default project/amount.
        if (Array.isArray(defaultAllocations) && defaultAllocations.length > 0) {
            setAllocations(defaultAllocations.map((allocation) => {
                const seeded = makeAllocation(seedDirection);
                if (allocation.project_id != null) seeded.project = String(allocation.project_id);
                if (allocation.project_account_id != null) {
                    seeded.projectAccountId = allocation.project_account_id;
                }
                if (allocation.amount != null && allocation.amount !== '') {
                    seeded.amount = String(allocation.amount);
                }
                if (allocation.direction) seeded.direction = allocation.direction;
                return seeded;
            }));
        } else {
            const first = makeAllocation(seedDirection);
            if (defaultProjectId) first.project = String(defaultProjectId);
            if (defaultAmount != null && defaultAmount !== '') {
                first.amount = String(defaultAmount);
            }
            setAllocations([first]);
        }
    }, [
        defaultAccount,
        defaultProjectId,
        defaultAllocations,
        defaultDate,
        defaultAmount,
        defaultDescription,
        defaultSupplierId,
        defaultTransactionType,
    ]);

    useEffect(() => {
        if (!open) return;
        resetState();
    }, [open, resetState]);

    // Load reference data when opened. Accounts and projects are bounded sets.
    // Activities and suppliers are searched server-side (one page, then scroll/search).
    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const [accountsData, projectsData] = await Promise.all([
                    accountsApi.getAll(),
                    projectsApi.getAll(),
                ]);
                if (cancelled) return;
                setAccounts(Array.isArray(accountsData) ? accountsData : []);
                setProjects(Array.isArray(projectsData) ? projectsData : []);
            } catch (err) {
                console.error('Failed to load wizard data:', err);
                if (!cancelled) setError(t('app.addTransactionWizard.loadError'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, t]);

    // Suppliers: (re)load the first page on open and whenever the debounced search
    // term changes, merging the selected supplier so it stays visible.
    useEffect(() => {
        if (!open) return undefined;
        const seq = ++supplierSeqRef.current;
        let cancelled = false;
        setSuppliersRefreshing(true);
        nextSupplierPageRef.current = 2;
        setSuppliersHasMore(true);
        (async () => {
            try {
                const data = await suppliersApi.list({
                    page: 1,
                    page_size: LIST_PAGE_SIZE,
                    search: supplierSearchDebounced || undefined,
                });
                if (cancelled || seq !== supplierSeqRef.current) return;
                let merged = data.results || [];
                const sid = selectedSupplierIdRef.current;
                if (sid != null && sid !== '' && !merged.some((s) => String(s.id) === String(sid))) {
                    try {
                        const extra = await suppliersApi.getById(sid);
                        if (seq !== supplierSeqRef.current) return;
                        if (extra) {
                            merged = [...merged, extra];
                        }
                    } catch (e) {
                        console.error('Failed to merge selected supplier into list:', e);
                    }
                }
                if (seq !== supplierSeqRef.current) return;
                setSuppliers(merged);
                setSuppliersHasMore(!!data.next);
            } catch (e) {
                console.error('Failed to load suppliers:', e);
            } finally {
                if (!cancelled && seq === supplierSeqRef.current) setSuppliersRefreshing(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, supplierSearchDebounced]);

    const fetchMoreSuppliers = useCallback(async () => {
        if (!suppliersHasMore || suppliersLoadingMore || suppliersRefreshing) return;
        setSuppliersLoadingMore(true);
        try {
            const page = nextSupplierPageRef.current;
            const data = await suppliersApi.list({
                page,
                page_size: LIST_PAGE_SIZE,
                search: supplierSearchDebounced || undefined,
            });
            setSuppliers((prev) => {
                const seen = new Set(prev.map((s) => String(s.id)));
                return [...prev, ...(data.results || []).filter((s) => !seen.has(String(s.id)))];
            });
            setSuppliersHasMore(!!data.next);
            nextSupplierPageRef.current = page + 1;
        } catch (e) {
            console.error('Failed to load more suppliers:', e);
        } finally {
            setSuppliersLoadingMore(false);
        }
    }, [suppliersHasMore, suppliersLoadingMore, suppliersRefreshing, supplierSearchDebounced]);

    const handleSupplierListScroll = useCallback((event) => {
        const el = event.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
            fetchMoreSuppliers();
        }
    }, [fetchMoreSuppliers]);

    useEffect(() => {
        if (!open) return undefined;
        const seq = ++activitySeqRef.current;
        let cancelled = false;
        setActivitiesRefreshing(true);
        nextActivityPageRef.current = 2;
        setActivitiesHasMore(true);
        (async () => {
            try {
                const data = await activitiesApi.list({
                    page: 1,
                    page_size: LIST_PAGE_SIZE,
                    search: activitySearchDebounced || undefined,
                });
                if (cancelled || seq !== activitySeqRef.current) return;
                let merged = Array.isArray(data.results) ? [...data.results] : [];
                const selectedIds = selectedActivityIdsRef.current || [];
                const missing = selectedIds.filter(
                    (aid) => !merged.some((a) => String(a.id) === String(aid)),
                );
                if (missing.length) {
                    const extras = await Promise.all(missing.map(async (aid) => {
                        try {
                            return await activitiesApi.getById(aid);
                        } catch (e) {
                            console.error('Failed to merge selected activity into list:', e);
                            return null;
                        }
                    }));
                    if (seq !== activitySeqRef.current) return;
                    extras.filter(Boolean).forEach((extra) => {
                        if (!merged.some((a) => String(a.id) === String(extra.id))) {
                            merged.push(extra);
                        }
                    });
                }
                if (seq !== activitySeqRef.current) return;
                setActivities(merged);
                setActivitiesHasMore(!!data.next);
            } catch (e) {
                console.error('Failed to load activities:', e);
            } finally {
                if (!cancelled && seq === activitySeqRef.current) setActivitiesRefreshing(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, activitySearchDebounced]);

    const fetchMoreActivities = useCallback(async () => {
        if (!activitiesHasMore || activitiesLoadingMore || activitiesRefreshing) return;
        setActivitiesLoadingMore(true);
        try {
            const page = nextActivityPageRef.current;
            const data = await activitiesApi.list({
                page,
                page_size: LIST_PAGE_SIZE,
                search: activitySearchDebounced || undefined,
            });
            setActivities((prev) => {
                const seen = new Set(prev.map((a) => String(a.id)));
                return [...prev, ...(data.results || []).filter((a) => !seen.has(String(a.id)))];
            });
            setActivitiesHasMore(!!data.next);
            nextActivityPageRef.current = page + 1;
        } catch (e) {
            console.error('Failed to load more activities:', e);
        } finally {
            setActivitiesLoadingMore(false);
        }
    }, [activitiesHasMore, activitiesLoadingMore, activitiesRefreshing, activitySearchDebounced]);

    const handleActivityListScroll = useCallback((event) => {
        const el = event.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
            fetchMoreActivities();
        }
    }, [fetchMoreActivities]);

    // Lazy-load project accounts for each selected project.
    useEffect(() => {
        if (!open) return;
        const projectIds = Array.from(new Set(
            allocations.map((a) => a.project).filter(Boolean),
        ));
        projectIds.forEach((pid) => {
            if (projectAccountsByProject[pid] !== undefined) return;
            setProjectAccountsByProject((prev) => ({ ...prev, [pid]: prev[pid] ?? null }));
            projectAccountsApi.getByProject(pid)
                .then((data) => {
                    setProjectAccountsByProject((prev) => ({
                        ...prev,
                        [pid]: Array.isArray(data) ? data : [],
                    }));
                })
                .catch((err) => {
                    console.error('Failed to load project accounts:', err);
                    setProjectAccountsByProject((prev) => ({ ...prev, [pid]: [] }));
                });
        });
    }, [open, allocations, projectAccountsByProject]);

    // Auto-select the single matching project account per allocation.
    useEffect(() => {
        if (!transactionType) return;
        setAllocations((prev) => {
            let changed = false;
            const next = prev.map((a) => {
                if (!a.project || a.projectAccountId) return a;
                const accs = projectAccountsByProject[a.project];
                if (!Array.isArray(accs)) return a;
                const { resolved } = resolveTargetProjectAccount(transactionType, accs);
                if (resolved) {
                    changed = true;
                    return { ...a, projectAccountId: resolved.id };
                }
                return a;
            });
            return changed ? next : prev;
        });
    }, [projectAccountsByProject, transactionType]);

    // Clear any selected supplier when the type doesn't use one.
    useEffect(() => {
        if (!requiresSupplier && supplier) {
            setSupplier('');
            selectedSupplierIdRef.current = null;
            setSupplierInputDisplay('');
        }
    }, [requiresSupplier, supplier]);

    // Once the seeded/selected supplier appears in the loaded list, show its label
    // in the input (covers the QuickBooks prefill case where an id is passed in).
    useEffect(() => {
        if (!supplier) {
            setSupplierInputDisplay('');
            return;
        }
        const opt = suppliers.find((s) => String(s.id) === String(supplier));
        if (!opt) return;
        setSupplierInputDisplay((prev) => (prev === '' ? formatSupplierForAutocomplete(opt) : prev));
    }, [supplier, suppliers]);

    const updateAllocation = (key, patch) => {
        setAllocations((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch } : a)));
        if (Object.prototype.hasOwnProperty.call(patch, 'activity')) {
            const aid = patch.activity;
            if (aid != null && aid !== ''
                && !selectedActivityIdsRef.current.some((id) => String(id) === String(aid))) {
                selectedActivityIdsRef.current = [...selectedActivityIdsRef.current, aid];
            }
        }
    };

    const addAllocation = () => setAllocations((prev) => [
        ...prev,
        makeAllocation(direction || TRANSACTION_EXPENSE),
    ]);
    const removeAllocation = (key) => setAllocations((prev) => (
        prev.length <= 1 ? prev : prev.filter((a) => a.key !== key)
    ));

    // Predetermined types own the direction. Existing rows are created as expense
    // before a type is chosen, so keep them in sync with the header.
    useEffect(() => {
        if (isCustomType || !direction) return;
        setAllocations((prev) => {
            if (prev.every((row) => row.direction === direction)) return prev;
            return prev.map((row) => (
                row.direction === direction ? row : { ...row, direction }
            ));
        });
    }, [direction, isCustomType]);

    const totalAmount = parseFloat(amount) || 0;
    const headerDirection = direction || TRANSACTION_EXPENSE;
    const signedAllocatedTotal = useMemo(
        () => signedAllocationsTotal(allocations, headerDirection),
        [allocations, headerDirection],
    );
    const amountsMatch = allocationsBalanceHeader(totalAmount, headerDirection, allocations);

    const formatSignedCurrency = (magnitude) => fmtMoney(magnitude);

    const projectById = useMemo(() => {
        const map = {};
        projects.forEach((p) => { map[p.id] = p; });
        return map;
    }, [projects]);

    const supplierById = useMemo(() => {
        const map = {};
        suppliers.forEach((s) => { map[String(s.id)] = s; });
        return map;
    }, [suppliers]);

    const activityById = useMemo(() => {
        const map = {};
        activities.forEach((a) => { map[String(a.id)] = a; });
        return map;
    }, [activities]);

    // Split a project's accounts into Recommended (matching the selected type) and
    // the remaining "All" accounts. Recommendation uses the frontend-known
    // transaction-type -> account-type mapping.
    const resolveAllocation = useCallback((a) => {
        const accs = projectAccountsByProject[a.project];
        if (!Array.isArray(accs)) {
            return { loading: Boolean(a.project), all: [], recommended: [], others: [] };
        }
        const { accountTypes } = transactionType
            ? resolveTargetProjectAccount(transactionType, accs)
            : { accountTypes: [] };
        const recommended = accs.filter((pa) => accountTypes.includes(pa.account_type));
        const recIds = new Set(recommended.map((r) => r.id));
        const others = accs.filter((pa) => !recIds.has(pa.id));
        return { loading: false, all: accs, recommended, others };
    }, [projectAccountsByProject, transactionType]);

    // Activities the selected supplier is known to realize (recommended), plus the rest.
    const selectedSupplier = supplierById[String(supplier)] || null;
    const supplierActivityIds = useMemo(
        () => new Set((selectedSupplier?.activities || []).map((a) => String(a.id))),
        [selectedSupplier],
    );
    const activityOptions = useMemo(() => {
        const seen = new Set();
        const options = [];
        const add = (activity) => {
            if (activity == null || activity.id == null || activity.id === '') return;
            const key = String(activity.id);
            if (seen.has(key)) return;
            seen.add(key);
            options.push(activity);
        };
        activities.forEach(add);
        allocations.forEach((row) => {
            if (!row.activity) return;
            add(activityById[String(row.activity)] || { id: row.activity, name: row.activityName || '' });
        });
        if (!supplierActivityIds.size) return options;
        return [
            ...options.filter((a) => supplierActivityIds.has(String(a.id))),
            ...options.filter((a) => !supplierActivityIds.has(String(a.id))),
        ];
    }, [activities, supplierActivityIds, allocations, activityById]);

    const step1Valid = Boolean(
        date
        && account
        && transactionType
        && totalAmount > 0
        && (!requiresSupplier || supplier)
        && direction,
    );

    const allocationValid = useCallback((a) => {
        if (!a.project) return false;
        if (!(parseFloat(a.amount) > 0)) return false;
        const { recommended } = resolveAllocation(a);
        const chosen = a.projectAccountId || (recommended.length === 1 ? recommended[0].id : null);
        return Boolean(chosen);
    }, [resolveAllocation]);

    const step2Valid = allocations.length > 0 && allocations.every(allocationValid) && amountsMatch;

    const buildPayload = () => ({
        date,
        account,
        supplier: requiresSupplier ? supplier : null,
        direction,
        description,
        amount: totalAmount,
        allocations: allocations.map((a) => {
            const { recommended } = resolveAllocation(a);
            const projectAccountId = a.projectAccountId
                || (recommended.length === 1 ? recommended[0].id : null);
            return {
                project: a.project,
                project_account: projectAccountId,
                // Activity/materials only apply to supplier-facing flows.
                activity: requiresSupplier ? (a.activity || null) : null,
                is_materials: requiresSupplier ? a.isMaterials : false,
                direction: isCustomType ? (a.direction || headerDirection) : headerDirection,
                amount: parseFloat(a.amount),
            };
        }),
    });

    const handleSubmit = async (acknowledgeNotDuplicate = false) => {
        try {
            setSubmitting(true);
            setError(null);
            const payload = {
                ...buildPayload(),
                acknowledge_not_duplicate: Boolean(acknowledgeNotDuplicate),
            };
            if (beforeSubmit) {
                const validation = beforeSubmit(payload);
                if (validation && validation.allowed === false) {
                    setError(t('app.quickbooksReconciliation.createFromQb.reconcileBlocked'));
                    return;
                }
            }
            const response = await transactionsApi.createAllocated(payload);
            setSimilarTransactions([]);
            if (onSubmitResponse) {
                await onSubmitResponse(response);
            } else {
                onSaved?.();
            }
            onClose();
        } catch (err) {
            console.error('Failed to create transaction:', err);
            const similar = err.response?.data?.similar_transactions;
            if (err.response?.status === 409 && Array.isArray(similar) && similar.length) {
                setSimilarTransactions(similar);
                setActiveStep(3);
                setError(null);
                return;
            }
            setSimilarTransactions([]);
            const apiErrors = err.response?.data?.errors;
            setError(
                (Array.isArray(apiErrors) ? apiErrors.join(' ') : null)
                || err.response?.data?.error
                || t('app.addTransactionWizard.submitError'),
            );
        } finally {
            setSubmitting(false);
        }
    };

    const showSimilarStep = similarTransactions.length > 0 || activeStep === 3;
    const steps = [
        t('app.addTransactionWizard.step1'),
        t('app.addTransactionWizard.step2'),
        t('app.addTransactionWizard.step3'),
        ...(showSimilarStep ? [t('app.addTransactionWizard.step4')] : []),
    ];

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>{titleOverride || t('app.addTransactionWizard.title')}</DialogTitle>
            <DialogContent dividers>
                {infoBanner}
                <Stepper activeStep={activeStep} sx={{ mb: 3, mt: infoBanner ? 2 : 0 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" py={5}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {activeStep === 0 && (
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label={t('app.addTransactionWizard.date')}
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={accounts}
                                        getOptionLabel={(a) => (typeof a === 'string' ? a : a.name || '')}
                                        value={accounts.find((a) => a.name === account) || null}
                                        onChange={(e, v) => setAccount(v ? v.name : '')}
                                        isOptionEqualToValue={(o, v) => o.name === v?.name}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                label={t('app.addTransactionWizard.financialAccount')}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        required
                                        label={t('app.addTransactionWizard.transactionType')}
                                        value={transactionType}
                                        onChange={(e) => setTransactionType(e.target.value)}
                                    >
                                        {WIZARD_TXN_TYPES.map((id) => (
                                            <MenuItem key={id} value={id}>
                                                {t(transactionTypeLabelKey(id))}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    {direction && !isCustomType && (
                                        <Box mt={1}>
                                            <TransactionDirectionBadge direction={direction} />
                                        </Box>
                                    )}
                                </Grid>
                                {isCustomType && (
                                    <Grid item xs={12} sm={6}>
                                        <TransactionDirectionToggleGroup
                                            fullWidth
                                            value={customDirection}
                                            onChange={setCustomDirection}
                                        />
                                    </Grid>
                                )}
                                {requiresSupplier && (
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={suppliers}
                                            filterOptions={(opts) => opts}
                                            getOptionLabel={(s) => (typeof s === 'string' ? s : formatSupplierForAutocomplete(s))}
                                            value={supplierById[String(supplier)] || null}
                                            onChange={(e, v) => {
                                                setSupplier(v ? v.id : '');
                                                selectedSupplierIdRef.current = v ? v.id : null;
                                                setSupplierInputDisplay(v ? formatSupplierForAutocomplete(v) : '');
                                            }}
                                            inputValue={supplierInputDisplay}
                                            onInputChange={(e, val, reason) => {
                                                if (reason === 'input') {
                                                    setSupplierInputDisplay(val);
                                                    setSupplierInput(val);
                                                } else if (reason === 'clear') {
                                                    setSupplierInputDisplay('');
                                                    setSupplierInput('');
                                                }
                                            }}
                                            loading={suppliersRefreshing || suppliersLoadingMore}
                                            ListboxProps={{
                                                onScroll: handleSupplierListScroll,
                                                sx: { maxHeight: 280, overflow: 'auto' },
                                            }}
                                            isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    label={t('app.addTransactionWizard.supplier')}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {(suppliersRefreshing || suppliersLoadingMore) ? (
                                                                    <CircularProgress color="inherit" size={16} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={t('app.addTransactionWizard.description')}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="number"
                                        label={t('app.addTransactionWizard.totalAmount')}
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        inputProps={{ step: '0.01', min: '0' }}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {activeStep === 1 && (
                            <Box>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 3,
                                        bgcolor: 'background.paper',
                                        pt: 0.5,
                                        pb: 1.5,
                                        mb: 1,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Box sx={{ px: 0.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                                                <StorefrontIcon sx={{ fontSize: 18 }} color="action" />
                                                <Typography variant="subtitle1" fontWeight={700} noWrap>
                                                    {selectedSupplier
                                                        ? formatSupplierForAutocomplete(selectedSupplier)
                                                        : (transactionType ? t(transactionTypeLabelKey(transactionType)) : '—')}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
                                                <CalendarTodayIcon sx={{ fontSize: 15 }} />
                                                <Typography variant="body2">{date}</Typography>
                                            </Stack>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mt={1}>
                                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                                                <NotesIcon sx={{ fontSize: 15 }} color="action" />
                                                <Typography variant="body2" color="text.secondary" fontStyle="italic" noWrap>
                                                    {description || '—'}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                                                {direction && (
                                                    <TransactionDirectionBadge direction={direction} />
                                                )}
                                                <Box
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        px: 1.25,
                                                        py: 0.25,
                                                        bgcolor: 'background.paper',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        fontFamily="monospace"
                                                        fontWeight={700}
                                                        color={direction === 'income' ? 'success.main' : 'text.primary'}
                                                    >
                                                        {fmtMoney(totalAmount)}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Box>
                                <Stack spacing={2}>
                                {allocations.map((a, index) => {
                                    const { all: allAccounts, recommended, others, loading: accLoading } = resolveAllocation(a);
                                    const showAccountSelect = a.project && allAccounts.length >= 1;
                                    const accountValue = a.projectAccountId
                                        || (recommended.length === 1 ? recommended[0].id : '');
                                    const noAccount = a.project && !accLoading && allAccounts.length === 0;
                                    return (
                                        <Card key={a.key} variant="outlined">
                                            <CardContent>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                                    <Typography variant="subtitle2">
                                                        {t('app.addTransactionWizard.allocation', { n: index + 1 })}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeAllocation(a.key)}
                                                        disabled={allocations.length <= 1}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Autocomplete
                                                            options={projects}
                                                            getOptionLabel={(p) => (typeof p === 'string' ? p : `${p.location} (${p.id})`)}
                                                            value={projectById[a.project] || null}
                                                            onChange={(e, v) => updateAllocation(a.key, {
                                                                project: v ? v.id : '',
                                                                projectAccountId: '',
                                                            })}
                                                            isOptionEqualToValue={(o, v) => o.id === v?.id}
                                                            renderInput={(params) => (
                                                                <TextField {...params} required label={t('app.addTransactionWizard.project')} />
                                                            )}
                                                        />
                                                        {noAccount && (
                                                            <Typography variant="caption" color="error" display="block" mt={0.5}>
                                                                {t('app.addTransactionWizard.noProjectAccount')}
                                                            </Typography>
                                                        )}
                                                    </Grid>
                                                    {showAccountSelect && (
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                select
                                                                fullWidth
                                                                required
                                                                label={t('app.addTransactionWizard.projectAccount')}
                                                                value={accountValue}
                                                                onChange={(e) => updateAllocation(a.key, { projectAccountId: e.target.value })}
                                                            >
                                                                {recommended.length > 0 && (
                                                                    <ListSubheader>{t('app.addTransactionWizard.recommended')}</ListSubheader>
                                                                )}
                                                                {recommended.map((pa) => (
                                                                    <MenuItem key={pa.id} value={pa.id}>
                                                                        {pa.name} · {t(projectAccountTypeLabelKey(pa.account_type))}
                                                                    </MenuItem>
                                                                ))}
                                                                {others.length > 0 && (
                                                                    <ListSubheader>{t('app.addTransactionWizard.all')}</ListSubheader>
                                                                )}
                                                                {others.map((pa) => (
                                                                    <MenuItem key={pa.id} value={pa.id}>
                                                                        {pa.name} · {t(projectAccountTypeLabelKey(pa.account_type))}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </Grid>
                                                    )}
                                                    {requiresSupplier && (
                                                        <Grid item xs={12} sm={6}>
                                                            <Autocomplete
                                                                options={activityOptions}
                                                                filterOptions={(opts) => opts}
                                                                groupBy={supplierActivityIds.size
                                                                    ? (ac) => (supplierActivityIds.has(String(ac.id))
                                                                        ? t('app.addTransactionWizard.recommended')
                                                                        : t('app.addTransactionWizard.all'))
                                                                    : undefined}
                                                                getOptionLabel={(ac) => (typeof ac === 'string' ? ac : (ac.name || ''))}
                                                                value={a.activity
                                                                    ? (activityOptions.find((ac) => String(ac.id) === String(a.activity)) || null)
                                                                    : null}
                                                                onChange={(e, v) => updateAllocation(a.key, {
                                                                    activity: v ? v.id : '',
                                                                    activityName: v ? (v.name || '') : '',
                                                                })}
                                                                onInputChange={(event, newInputValue, reason) => {
                                                                    if (reason === 'input') setActivityInput(newInputValue);
                                                                    else if (reason === 'clear') setActivityInput('');
                                                                }}
                                                                ListboxProps={{
                                                                    onScroll: handleActivityListScroll,
                                                                    sx: { maxHeight: 280, overflow: 'auto' },
                                                                }}
                                                                isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                                                                loading={activitiesRefreshing || activitiesLoadingMore}
                                                                renderOption={({ key, ...props }, ac) => (
                                                                    <li key={ac.id ?? key} {...props}>
                                                                        {ac.name}
                                                                    </li>
                                                                )}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} label={t('app.addTransactionWizard.activity')} />
                                                                )}
                                                            />
                                                        </Grid>
                                                    )}
                                                    {isCustomType && (
                                                        <Grid item xs={12} sm={6}>
                                                            <TransactionDirectionToggleGroup
                                                                fullWidth
                                                                value={a.direction || headerDirection}
                                                                onChange={(value) => updateAllocation(a.key, { direction: value })}
                                                            />
                                                        </Grid>
                                                    )}
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            required
                                                            type="number"
                                                            label={t('app.addTransactionWizard.projectAmount')}
                                                            value={a.amount}
                                                            onChange={(e) => updateAllocation(a.key, { amount: e.target.value })}
                                                            inputProps={{ step: '0.01', min: '0' }}
                                                        />
                                                    </Grid>
                                                    {requiresSupplier && (
                                                        <Grid item xs={12} sm={6}>
                                                            <FormControlLabel
                                                                control={(
                                                                    <Checkbox
                                                                        checked={a.isMaterials}
                                                                        onChange={(e) => updateAllocation(a.key, { isMaterials: e.target.checked })}
                                                                    />
                                                                )}
                                                                label={t('app.addTransactionWizard.isMaterials')}
                                                            />
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    );
                                })}

                                <Box>
                                    <Button startIcon={<AddIcon />} onClick={addAllocation} variant="outlined">
                                        {t('app.addTransactionWizard.addAllocation')}
                                    </Button>
                                </Box>

                                <Divider />
                                <Stack direction="row" justifyContent="space-between">
                                    <TransactionAmountWithDirection
                                        amount={totalAmount}
                                        direction={headerDirection}
                                        formatMagnitude={formatSignedCurrency}
                                        variant="body2"
                                        fontWeight={600}
                                    />
                                    <TransactionAmountWithDirection
                                        amount={Math.abs(signedAllocatedTotal)}
                                        direction={directionFromSignedValue(signedAllocatedTotal)}
                                        formatMagnitude={formatSignedCurrency}
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{ color: amountsMatch ? undefined : 'error.main' }}
                                    />
                                </Stack>
                                {!amountsMatch && (
                                    <Typography variant="caption" color="error">
                                        {t('app.addTransactionWizard.mustMatch')}
                                    </Typography>
                                )}
                                </Stack>
                            </Box>
                        )}

                        {activeStep === 2 && (
                            <Stack spacing={2}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {t('app.addTransactionWizard.step1')}
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.date')}</Typography><Typography variant="body2">{date}</Typography></Grid>
                                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.financialAccount')}</Typography><Typography variant="body2">{account}</Typography></Grid>
                                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.transactionType')}</Typography><Typography variant="body2">{transactionType ? t(transactionTypeLabelKey(transactionType)) : '—'}</Typography></Grid>
                                            <Grid item xs={6}>
                                                {direction
                                                    ? <TransactionDirectionBadge direction={direction} />
                                                    : <Typography variant="body2">—</Typography>}
                                            </Grid>
                                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.supplier')}</Typography><Typography variant="body2">{requiresSupplier ? (supplierById[String(supplier)] ? formatSupplierForAutocomplete(supplierById[String(supplier)]) : supplier) : '—'}</Typography></Grid>
                                            <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.totalAmount')}</Typography><Typography variant="body2">{fmtMoney(totalAmount)}</Typography></Grid>
                                            <Grid item xs={12}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.description')}</Typography><Typography variant="body2">{description || '—'}</Typography></Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                                {allocations.map((a, index) => {
                                    const { all: allAccounts, recommended } = resolveAllocation(a);
                                    const chosenId = a.projectAccountId || (recommended.length === 1 ? recommended[0].id : null);
                                    const pa = allAccounts.find((c) => c.id === chosenId);
                                    return (
                                        <Card key={a.key} variant="outlined">
                                            <CardContent>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('app.addTransactionWizard.allocation', { n: index + 1 })}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.project')}</Typography><Typography variant="body2">{projectById[a.project]?.location || a.project}</Typography></Grid>
                                                    <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.projectAccount')}</Typography><Typography variant="body2">{pa ? pa.name : '—'}</Typography></Grid>
                                                    {requiresSupplier && (
                                                        <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.activity')}</Typography><Typography variant="body2">{a.activity ? (activityById[String(a.activity)]?.name || a.activityName || a.activity) : '—'}</Typography></Grid>
                                                    )}
                                                    {requiresSupplier && (
                                                        <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.isMaterials')}</Typography><Typography variant="body2">{a.isMaterials ? t('common.yes') : t('common.no')}</Typography></Grid>
                                                    )}
                                                    <Grid item xs={6}><Typography variant="body2" color="text.secondary">{t('app.addTransactionWizard.projectAmount')}</Typography><Typography variant="body2">{fmtMoney(a.amount)}</Typography></Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Stack>
                        )}

                        {activeStep === 3 && (
                            <Stack spacing={1.5}>
                                <Alert severity="warning">
                                    {t('app.addTransactionWizard.similarFound')}
                                </Alert>
                                <CompactTransactionCard
                                    title={t('app.addTransactionWizard.yourTransaction')}
                                    date={{
                                        label: t('app.addTransactionWizard.date'),
                                        value: formatTxnDate(date),
                                    }}
                                    amount={{
                                        label: t('app.addTransactionWizard.totalAmount'),
                                        value: fmtMoney(totalAmount),
                                    }}
                                    supplier={{
                                        label: t('app.addTransactionWizard.supplier'),
                                        value: requiresSupplier
                                            ? (supplierById[String(supplier)]
                                                ? formatSupplierForAutocomplete(supplierById[String(supplier)])
                                                : supplier)
                                            : '—',
                                    }}
                                    project={{
                                        label: t('app.addTransactionWizard.project'),
                                        value: allocations
                                            .map((row) => projectById[row.project]?.location || row.project)
                                            .filter(Boolean)
                                            .join(', ') || '—',
                                    }}
                                    description={{
                                        label: t('app.addTransactionWizard.description'),
                                        value: description || '',
                                    }}
                                />
                                {similarTransactions.map((txn) => {
                                    const similarAllocations = Array.isArray(txn.project_transactions)
                                        ? txn.project_transactions
                                        : [];
                                    const projectLabel = similarAllocations
                                        .map((pt) => pt.project?.location || pt.project?.id)
                                        .filter(Boolean)
                                        .join(', ') || '—';
                                    const supplierLabel = txn.supplier
                                        ? [txn.supplier.name, txn.supplier.company].filter(Boolean).join(' · ')
                                        : '—';
                                    return (
                                        <CompactTransactionCard
                                            key={txn.id}
                                            warning
                                            title={t('app.addTransactionWizard.similarTransaction', { id: txn.id })}
                                            date={{
                                                label: t('app.addTransactionWizard.date'),
                                                value: formatTxnDate(txn.date),
                                            }}
                                            amount={{
                                                label: t('app.addTransactionWizard.totalAmount'),
                                                value: fmtMoney(txn.amount),
                                            }}
                                            supplier={{
                                                label: t('app.addTransactionWizard.supplier'),
                                                value: supplierLabel,
                                            }}
                                            project={{
                                                label: t('app.addTransactionWizard.project'),
                                                value: projectLabel,
                                            }}
                                            description={{
                                                label: t('app.addTransactionWizard.description'),
                                                value: txn.description || '',
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={submitting}>
                    {t('common.cancel')}
                </Button>
                <Box sx={{ flex: 1 }} />
                {activeStep > 0 && (
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => {
                            if (activeStep === 3) {
                                setSimilarTransactions([]);
                            }
                            setActiveStep((s) => s - 1);
                        }}
                        disabled={submitting}
                    >
                        {t('app.addTransactionWizard.back')}
                    </Button>
                )}
                {activeStep < 2 && (
                    <Button
                        variant="contained"
                        onClick={() => setActiveStep((s) => s + 1)}
                        disabled={loading || (activeStep === 0 ? !step1Valid : !step2Valid)}
                    >
                        {t('app.addTransactionWizard.next')}
                    </Button>
                )}
                {activeStep === 2 && (
                    <Button
                        variant="contained"
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                        onClick={() => handleSubmit(false)}
                        disabled={submitting || !step2Valid}
                    >
                        {submitting ? t('app.addTransactionWizard.creating') : t('app.addTransactionWizard.confirm')}
                    </Button>
                )}
                {activeStep === 3 && (
                    <Button
                        variant="contained"
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                        onClick={() => handleSubmit(true)}
                        disabled={submitting}
                    >
                        {submitting
                            ? t('app.addTransactionWizard.creating')
                            : t('app.addTransactionWizard.createAnyway')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
